import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SmilCaption } from "../../Core/Api/Types/smil-caption";
import { CreateMarker, FrameToTimecode, TimecodeToFrame, defaultTimecode } from "../../Utils/Timecode/ReactPlayer";
import { RootState } from "../redux-store";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { ObjectClass } from "../../Core/Api/Types/object-class";
import { Entry } from "@plussub/srt-vtt-parser/dist/types";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";

const magicNumbers = { "23.98": 0.9989920981 };

export interface ReduxStateReactPlayer {
  input: ReactPlayerInput;
  setup: ReactPlayerSetup;
  interface: ReactPlayerInterface;
}
interface ReactPlayerSetup {
  isPlaying: boolean;
  isFileTimecode: boolean;
  autoStart: boolean;
  playerReadyToSeek: boolean;
  mediaData: ReactPlayerData | null;
  image: string | undefined;
  playPause: boolean;
}

interface ReactPlayerInput {
  objectId: number | null;
  episode: number | null;
  shareMediaKey: string | null;
}

export interface AudioTrack {
  language: string;
  name: string;
}

export interface ReactPlayerMarker {
  markFrame: number;
  isSet: boolean;
}

interface ReactPlayerInterface {
  //   timeInSec: number;
  currentFrame: number;
  seek: { frame: number };
  isFileTCDisplayed: boolean;
  marker: { markIn: ReactPlayerMarker; markOut: ReactPlayerMarker };
  playbackSpeed: number;
  isPlaying: boolean;
  volume: number;
  background: "contrasted" | "default";
  fullScreen: boolean;
  isPip: boolean;
  languages: {
    activeCaptionIndex: number | null;
    activeAudioTrackIndex: number | null;
    captions: SmilCaption[];
    audioTracks: AudioTrack[];
  };
  vttThumbs: Entry[];
  isBuffering: boolean;
}

export class ReactPlayerData {
  parentId?: number = -1;
  frameInSeconds?: number = 0;
  timecodeIn?: string = defaultTimecode;
  frameRate: number = 0;
  totalFrameCount: number = 0;
  durationInSeconds: number = 0;
  frameDifferential: number = 1;
  objectId?: number = 0;
  objectType: ObjectType = ObjectType.Unknown;
  objectClass: ObjectClass = ObjectClass.Raw;
  objectLayer?: ObjectLayer = undefined;
  smilFullUrl?: string | null = null;
  captions?: SmilCaption[] = [];
  audioTracks?: AudioTrack[] = [];
  thumbs: boolean = false;
  message?: string = undefined;
  image?: string = undefined;
  videoStream: boolean = true;
}

const initialState: ReduxStateReactPlayer = {
  input: {
    objectId: null,
    episode: null,
    shareMediaKey: null,
  },
  setup: {
    isPlaying: false,
    isFileTimecode: false,
    autoStart: false,
    mediaData: null,
    image: undefined,
    playerReadyToSeek: false,
    playPause: true,
  },
  interface: {
    currentFrame: 0,
    playbackSpeed: 1,
    seek: { frame: 0 },
    isFileTCDisplayed: true,
    volume: 50,
    background: "default",
    fullScreen: false,
    isPip: false,
    marker: {
      markIn: { markFrame: 0, isSet: true },
      markOut: { markFrame: 0, isSet: true },
    },
    isPlaying: false,
    languages: { activeCaptionIndex: null, activeAudioTrackIndex: null, captions: [], audioTracks: [] },
    vttThumbs: [],
    isBuffering: false,
  },
};

const ReactPlayerSlice = createSlice({
  name: "ReactPlayer",
  initialState,

  reducers: {
    PlayPause: (state) => {
      state.setup.playPause = !state.setup.playPause;
    },
    SetPlayerInput: (state, action: PayloadAction<ReactPlayerInput>) => {
      state.input.objectId = action.payload.objectId;
      state.input.episode = action.payload.episode;
      state.input.shareMediaKey = action.payload.shareMediaKey;

      state.interface.currentFrame = 0;
      state.interface.isPlaying = false;
    },
    SetPlayerImage: (state, action: PayloadAction<string>) => {
      if (state.setup.mediaData) state.setup.mediaData.image = action.payload;
    },
    SetPlayerAutoStart: (state, action: PayloadAction<boolean>) => {
      state.setup.autoStart = action.payload;
    },
    SetMediaData: (state, action: PayloadAction<ReactPlayerData>) => {
      if (state.setup.mediaData !== null) {
        let changed = state.setup.mediaData.smilFullUrl !== action.payload.smilFullUrl;

        if (changed) console.log("changed smil url = " + action.payload.smilFullUrl);
      }

      state.setup.mediaData = action.payload;
    },

    SetVolume: (state, action: PayloadAction<number>) => {
      state.interface.volume = action.payload;
    },
    SetPlayerBackground: (state, action: PayloadAction<"contrasted" | "default">) => {
      state.interface.background = action.payload;
    },
    SetPlaybackSpeed: (state, action: PayloadAction<number>) => {
      state.interface.playbackSpeed = action.payload;
    },
    SetPlayerFullScreen: (state, action: PayloadAction<boolean>) => {
      state.interface.fullScreen = action.payload;
    },
    SetPlayerPip: (state, action: PayloadAction<boolean>) => {
      state.interface.isPip = action.payload;
    },
    SetVttThumbs: (state, action: PayloadAction<Entry[]>) => {
      state.interface.vttThumbs = action.payload;
    },
    UnsetInput: (state) => {
      state.input = {
        objectId: null,
        episode: null,
        shareMediaKey: null,
      };

      state.interface.currentFrame = 0;
      state.interface.isPlaying = false;
    },
    UnsetMediaData: (state) => {
      state.setup.mediaData = null;
      state.setup.image = undefined;
    },
    ResetReduxData: (state) => {
      state = initialState;
    },
    SetCurrentFrame: (state, action: PayloadAction<number>) => {
      // differential of media duration when played through an html video player
      // we fix this by recording at media load the duration differential, and factor in the % of media played
      const frameDifferential = state.setup.mediaData ? state.setup.mediaData.frameDifferential : 1;
      state.interface.currentFrame = action.payload / frameDifferential;
    },
    SetFrameDifferential: (state, action: PayloadAction<number>) => {
      state.setup.mediaData!.frameDifferential = action.payload;
    },
    SetSeek: (state, action: PayloadAction<number>) => {
      state.interface.seek = { frame: action.payload };
    },
    SetFileTCDisplayed: (state) => {
      state.interface.isFileTCDisplayed = !state.interface.isFileTCDisplayed;
    },
    SetActiveLanguages: (
      state,
      action: PayloadAction<{ activeCaptionIndex: number | null; activeAudioTrackIndex: number | null }>
    ) => {
      if (
        action.payload.activeCaptionIndex !== null ||
        (action.payload.activeAudioTrackIndex === null && action.payload.activeCaptionIndex === null)
      ) {
        state.interface.languages.activeCaptionIndex = action.payload.activeCaptionIndex;
      }
      if (
        action.payload.activeAudioTrackIndex !== null ||
        (action.payload.activeAudioTrackIndex === null && action.payload.activeCaptionIndex === null)
      ) {
        state.interface.languages.activeAudioTrackIndex = action.payload.activeAudioTrackIndex;
      }
    },
    SetAudioTracks: (state, action: PayloadAction<AudioTrack[]>) => {
      state.interface.languages.audioTracks = action.payload;
    },
    SetCaptions: (state, action: PayloadAction<SmilCaption[]>) => {
      state.interface.languages.captions = action.payload;
    },
    SetMarkInByFrame: (state, action: PayloadAction<number>) => {
      if (action.payload <= 0 || action.payload > state.setup.mediaData?.totalFrameCount!) return;
      if (state.interface.marker.markOut.markFrame < action.payload) {
        state.interface.marker.markOut = CreateMarker(action.payload);
      }
      state.interface.marker.markIn = CreateMarker(action.payload);
    },
    SetMarkOutByFrame: (state, action: PayloadAction<number>) => {
      if (
        state.interface.marker.markIn.markFrame > action.payload ||
        action.payload <= 0 ||
        action.payload > state.setup.mediaData?.totalFrameCount!
      ) {
        return;
      }
      state.interface.marker.markOut = CreateMarker(action.payload);
    },
    SetMarkByCurrentFrame: (state, action: PayloadAction<"In" | "Out">) => {
      if (action.payload === "In") {
        // replicate of SetMarkInBySec with state.interface.timeInSec in param
        if (state.interface.currentFrame < 0 || state.interface.currentFrame > state.setup.mediaData?.totalFrameCount!)
          return;
        if (state.interface.marker.markOut.markFrame < state.interface.currentFrame) {
          state.interface.marker.markOut = CreateMarker(
            state.interface.currentFrame + state.setup.mediaData?.frameRate!
          );
        }
        state.interface.marker.markIn = CreateMarker(state.interface.currentFrame);
      } else if (action.payload === "Out") {
        // replicate of SetMarkOutBySec with state.interface.timeInSec in param
        let frame = state.interface.currentFrame;
        if (
          state.interface.marker.markIn.markFrame > frame ||
          frame <= 0 ||
          frame > state.setup.mediaData?.totalFrameCount! * state.setup.mediaData?.frameRate!
        )
          return;
        state.interface.marker.markOut = CreateMarker(frame);
      }
    },
    SetPlayingState: (state, action: PayloadAction<boolean>) => {
      state.interface.isPlaying = action.payload;
    },
    GoToFrame: (state, action: PayloadAction<number>) => {
      state.interface.seek = { frame: action.payload };
    },
    GoToFrameDelta: (state, action: PayloadAction<number>) => {
      let targetFrame = state.interface.currentFrame + action.payload;
      if (targetFrame < 0) targetFrame = 0;
      if (state.setup.mediaData?.totalFrameCount) {
        if (targetFrame > state.setup.mediaData?.totalFrameCount)
          targetFrame = state.setup.mediaData?.totalFrameCount - 1;
      }
      state.interface.seek = { frame: targetFrame };
    },
    SetReady: (state) => {
      state.setup.playerReadyToSeek = !state.setup.playerReadyToSeek;
    },
    GoToLastFrame: (state) => {
      if (
        state.setup.mediaData?.totalFrameCount &&
        state.setup.mediaData.totalFrameCount > 0 &&
        state.setup.mediaData.frameRate
      )
        state.interface.seek = {
          frame: state.setup.mediaData.totalFrameCount - 1,
        };
    },
    SetIsBuffering: (state, action) => {
      state.interface.isBuffering = action.payload;
    },
  },
});

export const {
  PlayPause,
  SetPlayerInput,
  SetMediaData,
  SetPlayerImage,
  SetPlayerAutoStart,
  SetCurrentFrame,
  SetFrameDifferential,
  SetVolume,
  SetPlayerBackground,
  SetPlaybackSpeed,
  SetPlayerFullScreen,
  SetPlayerPip,
  SetActiveLanguages,
  SetAudioTracks,
  SetCaptions,
  SetVttThumbs,
  SetSeek,
  SetFileTCDisplayed,
  SetMarkInByFrame,
  SetMarkOutByFrame,
  SetMarkByCurrentFrame,
  SetPlayingState,
  GoToFrame,
  GoToFrameDelta,
  SetReady,
  UnsetMediaData,
  ResetReduxData,
  UnsetInput,
  GoToLastFrame,
  SetIsBuffering,
} = ReactPlayerSlice.actions;

export const GetPlayerInput = (state: RootState): ReactPlayerInput => state.reactPlayer.input;
export const GetPlayingState = (state: RootState): boolean => state.reactPlayer.interface.isPlaying;
export const GetPlayerSetup = (state: RootState): ReactPlayerSetup => state.reactPlayer.setup;
export const GetPlayerSetupData = (state: RootState): ReactPlayerData => state.reactPlayer.setup.mediaData!;
export const GetVolume = (state: RootState): number => state.reactPlayer.interface.volume;
export const GetPlayerBackground = (state: RootState): "contrasted" | "default" =>
  state.reactPlayer.interface.background;
export const GetPlayerImage = (state: RootState): string | undefined => {
  return state.reactPlayer.setup.mediaData?.image;
};

export const GetFrameDifferential = (state: RootState): number => state.reactPlayer.setup.mediaData?.frameDifferential!;
export const GetRealCurrentFrame = (state: RootState): number => state.reactPlayer.interface.currentFrame;
export const GetTimeInSec = (state: RootState): number =>
  state.reactPlayer.interface.currentFrame / state.reactPlayer.setup.mediaData?.frameRate!;
export const GetTotalFrameCount = (state: RootState): number => state.reactPlayer.setup.mediaData?.totalFrameCount!;
export const GetPlaybackSpeed = (state: RootState): number => state.reactPlayer.interface.playbackSpeed;
export const GetFullScreen = (state: RootState): boolean => state.reactPlayer.interface.fullScreen;
export const GetPlayerPip = (state: RootState): boolean => state.reactPlayer.interface.isPip;
export const GetLanguages = (state: RootState): ReactPlayerInterface["languages"] =>
  state.reactPlayer.interface.languages;
export const GetVttThumbFromTime =
  (time: number) =>
  (state: RootState): Entry | null => {
    const thumb = state.reactPlayer.interface.vttThumbs.find(
      (thumb) => thumb.from / 1000 <= time && thumb.to / 1000 >= time
    );
    if (thumb) return thumb;
    else return null;
  };
export const GetTimecodeIn = (state: RootState): string | undefined => state.reactPlayer.setup.mediaData?.timecodeIn;
export const GetPlayerSeek = (state: RootState): { frame: number } => state.reactPlayer.interface.seek;
export const GetFrameRate = (state: RootState): number => state.reactPlayer.setup.mediaData?.frameRate!;
export const GetIsFileTCDisplayed = (state: RootState): boolean => state.reactPlayer.interface.isFileTCDisplayed;

export const GetTimecode = createSelector([GetRealCurrentFrame, GetFrameRate], (currentFrame, frameRate) => {
  return FrameToTimecode(currentFrame, frameRate);
});

export const GetFileTimecode = createSelector(
  [GetRealCurrentFrame, GetFrameRate, (state: RootState) => state],
  (currentFrame, frameRate, state) => {
    const offset = state.reactPlayer.setup.mediaData?.timecodeIn;
    if (offset) return FrameToTimecode(currentFrame + TimecodeToFrame(offset, frameRate), frameRate);
    else return FrameToTimecode(currentFrame, frameRate);
  }
);

export const GetMarkers = (state: RootState): { markIn: ReactPlayerMarker; markOut: ReactPlayerMarker } =>
  state.reactPlayer.interface.marker;
export const GetIsReady = (state: RootState): boolean => state.reactPlayer.setup.playerReadyToSeek;
export const GetIsBuffering = (state: RootState): boolean => state.reactPlayer.interface.isBuffering;

export default ReactPlayerSlice.reducer;
